import { Suspense, type ReactNode } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { useVersionCheck } from "@/hooks/useVersionCheck";

const VersionCheck = () => { useVersionCheck(); return null; };
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { BrandingProvider } from "@/components/branding/BrandingProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { RoleBasedLayout } from "@/components/layouts/RoleBasedLayout";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";


// Eager — small, critical, or used on first paint.
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";

// Lazy — split into per-route chunks so the initial JS bundle is small.
// Visiting any one route only downloads that route's chunk on demand.
const Analytics = lazy(() => import("./pages/Analytics"));
const Clients = lazy(() => import("./pages/Clients"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignDetails = lazy(() => import("./pages/CampaignDetails"));
const CampaignSettings = lazy(() => import("./pages/CampaignSettings"));
const CreateCampaign = lazy(() => import("./pages/CreateCampaign"));
const NewCampaign = lazy(() => import("./pages/NewCampaign"));
const ManagedCampaignWizard = lazy(() => import("@/components/managed-campaign/ManagedCampaignWizard").then(m => ({ default: m.ManagedCampaignWizard })));
const SimplifiedOnboardingWizard = lazy(() => import("@/components/simplified-onboarding/SimplifiedOnboardingWizard").then(m => ({ default: m.SimplifiedOnboardingWizard })));
const HowManagedCampaignsWorkPage = lazy(() => import("@/pages/HowManagedCampaignsWorkPage").then(m => ({ default: m.HowManagedCampaignsWorkPage })));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Settings = lazy(() => import("./pages/Settings"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const JobQueueMonitor = lazy(() => import("./pages/JobQueueMonitor"));
const SystemCapacity = lazy(() => import("./pages/SystemCapacity"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const GuestOnboarding = lazy(() => import("./pages/campaigns/GuestOnboarding").then(m => ({ default: m.GuestOnboarding })));
const DemoOnboarding = lazy(() => import("./pages/campaigns/DemoOnboarding").then(m => ({ default: m.DemoOnboarding })));
const EnterpriseBilling = lazy(() => import("./pages/EnterpriseBilling"));
const CompanyKnowledge = lazy(() => import("./pages/CompanyKnowledge"));
const Crm = lazy(() => import("./pages/Crm"));
const CrmLead = lazy(() => import("./pages/CrmLead"));
const CampaignAnalytics = lazy(() => import("./pages/CampaignAnalytics"));
const PublicRenewal = lazy(() => import("./pages/PublicRenewal"));
const SelfServePaidOnboarding = lazy(() => import("./pages/SelfServePaidOnboarding"));

// Admin pages — heavy tables, only loaded when super admin actually visits.
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Organizations = lazy(() => import("./pages/admin/Organizations"));
const WorkspaceDetail = lazy(() => import("./pages/admin/WorkspaceDetail"));
const WorkspaceProjection = lazy(() => import("./pages/admin/WorkspaceProjection"));
const WorkspaceSessions = lazy(() => import("./pages/admin/WorkspaceSessions"));
const Users = lazy(() => import("./pages/admin/Users"));
const ManagedCampaigns = lazy(() => import("./pages/admin/ManagedCampaigns"));
const CampaignMonitor = lazy(() => import("./pages/admin/CampaignMonitor"));
const TargetRefillHub = lazy(() => import("./pages/admin/TargetRefillHub"));
const TargetRefillLogs = lazy(() => import("./pages/admin/TargetRefillLogs"));
const MasterAccounts = lazy(() => import("./pages/admin/MasterAccounts"));
const IndustryPerformance = lazy(() => import("./pages/admin/IndustryPerformance"));

const TargetInsightLeadDetail = lazy(() => import("./pages/admin/TargetInsightLeadDetail"));
const ConnectedAccountDetail = lazy(() => import("./pages/admin/ConnectedAccountDetail"));
const DisconnectedAccounts = lazy(() => import("./pages/admin/DisconnectedAccounts"));
const AccountRenewals = lazy(() => import("./pages/admin/AccountRenewals"));
const BlockedAccounts = lazy(() => import("./pages/admin/BlockedAccounts"));
const IdleAccounts = lazy(() => import("./pages/admin/IdleAccounts"));
const IcpAnalyzer = lazy(() => import("./pages/admin/IcpAnalyzer"));
const ProspectDetail = lazy(() => import("./pages/admin/ProspectDetail"));
const WorkspaceConnectionDetail = lazy(() => import("./pages/admin/WorkspaceConnectionDetail"));
const OrphanCampaigns = lazy(() => import("./pages/admin/OrphanCampaigns"));

const SelfServeLinks = lazy(() => import("./pages/admin/SelfServeLinks"));
const CronJobsRegistry = lazy(() => import("./pages/admin/CronJobsRegistry"));
const AdminAnalyticsIndex = lazy(() => import("./pages/admin/AdminAnalyticsIndex"));
const AdminSystemIndex = lazy(() => import("./pages/admin/AdminSystemIndex"));
const AdminCommunicationsIndex = lazy(() => import("./pages/admin/AdminCommunicationsIndex"));
const AiModelSettings = lazy(() => import("./pages/admin/AiModelSettings"));
const SequenceGaps = lazy(() => import("./pages/admin/SequenceGaps"));

const ProfileEngine = lazy(() => import("./pages/admin/ProfileEngine"));



const Team = lazy(() => import("./pages/admin/Team"));
const CustomerActivity = lazy(() => import("./pages/admin/CustomerActivity"));
const CrmCalendar = lazy(() => import("./pages/admin/CrmCalendar"));

// Suspense fallback — intentionally minimal so route chunk loads don't show a
// jarring full-screen spinner. A faint progress bar at the very top of the
// viewport is enough feedback without blanking the page.
const RouteFallback = () => (
  <div className="min-h-screen bg-background">
    <div className="fixed top-0 left-0 right-0 h-0.5 overflow-hidden z-[100] pointer-events-none">
      <div className="h-full w-1/3 bg-primary/70 animate-loading-bar" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache freshness window — within 30s, navigating between pages or
      // returning to the browser tab serves cached data instantly (no refetch).
      staleTime: 30_000,
      // Keep cache around for 5 minutes after a query becomes unused so
      // navigating Inbox → Campaign → Inbox → Campaign returns instantly.
      gcTime: 5 * 60_000,
      // Eliminates the "page reloads when I switch back to this tab" flicker.
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      retry: 1,
      // Exponential backoff on retries so transient failures don't fire two
      // back-to-back requests within 200ms (visible as a double-spinner).
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <VersionCheck />
        <BrowserRouter>
          <AuthProvider>
            <WorkspaceProvider>
              <BrandingProvider>
              <Suspense fallback={<RouteFallback />}>

              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/renew/:slug" element={<PublicRenewal />} />
                <Route path="/renew/:slug/:seatId" element={<PublicRenewal />} />
                <Route path="/start/:token" element={<SelfServePaidOnboarding />} />

                <Route
                  path="/admin/team"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <Team />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/crm-calendar" element={<ProtectedRoute><AdminRoute><AdminLayout><CrmCalendar /></AdminLayout></AdminRoute></ProtectedRoute>} />
                <Route path="/admin/account-renewals" element={<ProtectedRoute><AdminRoute><AdminLayout><AccountRenewals /></AdminLayout></AdminRoute></ProtectedRoute>} />
                <Route
                  path="/admin/customer-activity"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <CustomerActivity />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/partner/*" element={<Navigate to="/" replace />} />


                {/* Onboarding and invitation routes */}
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route path="/invite/:token" element={<Navigate to="/auth" replace />} />
                <Route
                  path="/onboarding/guest/:token"
                  element={<GuestOnboarding />}
                />
                <Route
                  path="/campaigns/onboarding/demo"
                  element={<DemoOnboarding />}
                />

                {/* Protected routes with role-based layout */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Dashboard />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Analytics />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Team Accounts - LinkedIn accounts connected to the workspace */}
                <Route
                  path="/linkedin-accounts"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Clients />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Legacy route for backward compatibility */}
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Clients />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Campaigns />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns/new"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <NewCampaign />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />


                <Route
                  path="/campaigns/onboarding"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <SimplifiedOnboardingWizard />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />


                <Route
                  path="/campaigns/managed"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <ManagedCampaignWizard />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns/managed/how-it-works"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <HowManagedCampaignsWorkPage />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns/create"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CreateCampaign />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns/:id"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CampaignDetails />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns/:id/settings"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CampaignSettings />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaigns/:id/sequence"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CampaignSettings />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaigns/:id/analytics"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <CampaignAnalytics />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/campaigns/:id/audience"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CampaignSettings />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/inbox"
                  element={
                    <ProtectedRoute>
                      <Inbox />
                    </ProtectedRoute>
                  }
                />

                <Route path="/leads" element={<Navigate to="/inbox" replace />} />
                <Route path="/leads/*" element={<Navigate to="/inbox" replace />} />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Settings />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route path="/team" element={<Navigate to="/" replace />} />

                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Navigate to="/billing/enterprise" replace />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/knowledge"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CompanyKnowledge />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/crm"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <Crm />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/crm/lead/:targetId"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CrmLead />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profiles"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CompanyKnowledge />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Alias for company-knowledge route for backward compatibility */}
                <Route
                  path="/company-knowledge"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <CompanyKnowledge />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/billing/enterprise"
                  element={
                    <ProtectedRoute>
                      <RoleBasedLayout>
                        <EnterpriseBilling />
                      </RoleBasedLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Admin-only routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <AdminDashboard />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/organizations"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <Organizations />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/industry-performance"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <IndustryPerformance />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/organizations/:workspaceId"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <WorkspaceDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/organizations/:workspaceId/accounts"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <WorkspaceDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/organizations/:workspaceId/campaigns"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <WorkspaceDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <Users />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/managed-campaigns"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ManagedCampaigns />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/target-refill"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <TargetRefillHub />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/target-refill/campaigns"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <CampaignMonitor view="campaigns" />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/target-refill/sn-links"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <CampaignMonitor view="sn_links" />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/target-refill/logs"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <TargetRefillLogs />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/campaign-monitor"
                  element={<Navigate to="/admin/target-refill" replace />}
                />

                <Route
                  path="/admin/orphan-campaigns"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <OrphanCampaigns />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route path="/admin/awaiting-limits" element={<Navigate to="/admin/system" replace />} />


                <Route path="/admin/critical-logs" element={<Navigate to="/admin/system" replace />} />


                <Route
                  path="/admin/self-serve-links"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <SelfServeLinks />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route path="/admin/pricing" element={<Navigate to="/admin" replace />} />

                <Route
                  path="/admin/organizations/:workspaceId/projection"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <WorkspaceProjection />
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/organizations/:workspaceId/sessions"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <WorkspaceSessions />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />


                <Route path="/admin/subscriptions" element={<Navigate to="/admin/system" replace />} />
                <Route path="/admin/whatsapp" element={<Navigate to="/admin/communications?tab=whatsapp" replace />} />
                <Route path="/admin/notifications" element={<Navigate to="/admin/communications?tab=emails" replace />} />
                <Route path="/admin/support-chatbot" element={<Navigate to="/admin/communications" replace />} />
                <Route path="/admin/teams-alerts" element={<Navigate to="/admin/communications?tab=teams" replace />} />
                <Route
                  path="/admin/master-accounts"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <MasterAccounts />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/replies" element={<Navigate to="/admin/organizations" replace />} />

                <Route
                  path="/admin/target-database"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ProfileEngine tab="database" />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/target-database/detail/:providerId"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ProspectDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/prospect-intelligence" element={<Navigate to="/admin/target-database" replace />} />
                <Route path="/admin/prospect-intelligence/detail/:providerId" element={<Navigate to="/admin/target-database" replace />} />

                <Route path="/admin/workspace-intelligence" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="/admin/workspace-intelligence/:workspaceId" element={<Navigate to="/admin/organizations" replace />} />

                <Route
                  path="/admin/analytics"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <AdminAnalyticsIndex />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/system"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <AdminSystemIndex />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/sequence-gaps"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <SequenceGaps />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/ai-models"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <AiModelSettings />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/target-db-enrichment"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ProfileEngine tab="enrichment" />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/profile-prefetch"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ProfileEngine tab="prefetch" />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/profile-engine"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ProfileEngine tab="overview" />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/communications"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <AdminCommunicationsIndex />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/target-insights" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="/admin/target-insights/:workspaceId" element={<Navigate to="/admin/organizations" replace />} />

                <Route
                  path="/admin/target-insights/:workspaceId/lead/:targetId"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <TargetInsightLeadDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/organizations/:workspaceId/connection/:targetId"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <WorkspaceConnectionDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/connected-accounts" element={<Navigate to="/admin/organizations" replace />} />
                <Route
                  path="/admin/connected-accounts/:clientId"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <ConnectedAccountDetail />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/disconnected-accounts"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <DisconnectedAccounts />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/blocked-accounts"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <BlockedAccounts />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/idle-accounts"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <IdleAccounts />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/auto-withdraw" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="/admin/manual-sending" element={<Navigate to="/admin/organizations" replace />} />

                <Route path="/admin/account-daily-usage" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="/admin/acceptance-rate" element={<Navigate to="/admin/organizations" replace />} />

                <Route
                  path="/admin/cron-jobs"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <CronJobsRegistry />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin/partners" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/partners/:partnerId" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/ai-credit-usage" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="/admin/daily-activity" element={<Navigate to="/admin/organizations" replace />} />
                <Route path="/admin/daily-activity/:workspaceId" element={<Navigate to="/admin/organizations" replace />} />

                <Route
                  path="/admin/icp-analyzer"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <IcpAnalyzer />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/job-queue"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <JobQueueMonitor />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/system-capacity"
                  element={
                    <ProtectedRoute>
                      <AdminRoute>
                        <AdminLayout>
                          <SystemCapacity />
                        </AdminLayout>
                      </AdminRoute>
                    </ProtectedRoute>
                  }
                />

                <Route path="/admin/auto-workspaces" element={<Navigate to="/admin" replace />} />






                {/* Member-only routes */}
                <Route path="/my-account" element={<Navigate to="/" replace />} />

                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </BrandingProvider>

            </WorkspaceProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
